import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { format } from 'date-fns';
import { Subject, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs';
import { PayeeButtonComponent } from 'src/app/shared/payee-buttons/payee-button/payee-button.component';
import { PayeeMiniIconButtonComponent } from 'src/app/shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { ChipDropdownComponent } from 'src/app/shared/payee-cases/chip-dropdown/chip-dropdown.component';
import { PayeeDatepickerComponent } from 'src/app/shared/payee-form-fields/payee-datepicker/payee-datepicker.component';
import { PayeeFormFieldComponent } from 'src/app/shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeIconComponent } from 'src/app/shared/payee-icon/payee-icon.component';
import { EventsService } from '../events.service';

@Component({
  selector: 'app-events-date-filter',
  templateUrl: './events-date-filter.component.html',
  styleUrls: ['./events-date-filter.component.scss'],
  imports: [
    ChipDropdownComponent,
    PayeeFormFieldComponent,
    PayeeDatepickerComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeButtonComponent,
    TranslateModule,
  ],
})
export class EventsDateFilterComponent implements OnInit, OnDestroy {
  private eventsService = inject(EventsService);
  private fb = inject(FormBuilder);

  private readonly destroy = new Subject<void>();

  readonly form = this.fb.group({
    from: this.fb.control<string | null>(null),
    to: this.fb.control<string | null>(null),
  });

  readonly max = new Date();
  dropdownOpen = false;

  get fromControl() {
    return this.form.controls.from;
  }
  get toControl() {
    return this.form.controls.to;
  }

  get selected(): boolean {
    return (
      !!this.eventsService.happened_at_min.value ||
      !!this.eventsService.happened_at_max.value
    );
  }

  ngOnInit(): void {
    this.eventsService.happened_at_min
      .pipe(
        takeUntil(this.destroy),
        startWith(this.eventsService.happened_at_min.value),
        map((v) => (v instanceof Date ? format(v, 'yyyy-MM-dd') : v)),
        distinctUntilChanged(),
      )
      .subscribe((v) => {
        this.fromControl.patchValue(v, { emitEvent: false });
      });

    this.eventsService.happened_at_max
      .pipe(
        takeUntil(this.destroy),
        startWith(this.eventsService.happened_at_max.value),
        map((v) => (v instanceof Date ? format(v, 'yyyy-MM-dd') : v)),
        distinctUntilChanged(),
      )
      .subscribe((v) => {
        this.toControl.patchValue(v, { emitEvent: false });
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  applyFilter(): void {
    this.eventsService.happened_at_min.next(this.fromControl.value);
    this.eventsService.happened_at_max.next(this.toControl.value);
    this.dropdownOpen = false;
  }

  clearFilter(): void {
    this.fromControl.patchValue(null);
    this.toControl.patchValue(null);
    this.eventsService.happened_at_min.next(null);
    this.eventsService.happened_at_max.next(null);
    this.dropdownOpen = false;
  }

  readonly displayWith = (): string => {
    const { happenedAtMin, happenedAtMax } = this.eventsService.getFilters();

    return `${happenedAtMin ?? ''} - ${happenedAtMax ?? ''}`.trim();
  };
}
